import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getReportGroups, getEmbedConfig } from '../services/reportService';
import { logout, toggleTestMode } from '../features/auth/authSlice';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import AiChatWidget from '../components/AiChatWidget';
import { startSession, endSession, logPage } from '../services/usageService';

const ReportsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    user, isAdmin, canAccessTest, isTestMode,
    primaryColor, secondaryColor, logoUrl, portalTitle
  } = useSelector((state) => state.auth);

  const [reportGroups, setReportGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [selectedReport, setSelectedReport] = useState(null);
  const [embedConfig, setEmbedConfig] = useState(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState([]);

  const embedRef = useRef(null);
  const sessionIdRef = useRef(null);
  const sessionStartRef = useRef(null);
  const currentPageRef = useRef(null);
  const pageStartRef = useRef(null);

  const brand = {
    primary: primaryColor || '#1a1a2e',
    secondary: secondaryColor || '#2e75b6',
    title: portalTitle || 'Avixa Portal',
    logo: logoUrl || null,
  };

  // Sayfa başlığı
  useEffect(() => {
    document.title = brand.title;
  }, [brand.title]);

  // Rapor gruplarını yükle
  useEffect(() => {
    fetchGroups();
  }, [isTestMode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sayfa kapanınca session'ı bitir
  useEffect(() => {
    const handleUnload = () => {
      if (sessionIdRef.current) {
        const duration = Math.floor((Date.now() - sessionStartRef.current) / 1000);
        const payload = JSON.stringify({
          sessionId: sessionIdRef.current,
          durationSeconds: duration
        });
        navigator.sendBeacon(
          `${process.env.REACT_APP_API_URL}/api/Log/session/end`,
          new Blob([payload], { type: 'application/json' })
        );
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, []);

  // Aktif sayfa polling (2 sn)
  useEffect(() => {
    const interval = setInterval(async () => {
      if (!embedRef.current || !sessionIdRef.current) return;

      try {
        const pages = await embedRef.current.getPages();
        const activePage = pages?.find(p => p.isActive);
        if (!activePage) return;

        const activePageName = activePage.displayName;

        if (currentPageRef.current && currentPageRef.current !== activePageName) {
          const duration = Math.floor((Date.now() - pageStartRef.current) / 1000);
          if (duration > 0) {
            logPage(sessionIdRef.current, currentPageRef.current, duration)
              .catch(() => {});
          }
          currentPageRef.current = activePageName;
          pageStartRef.current = Date.now();
        } else if (!currentPageRef.current) {
          currentPageRef.current = activePageName;
          pageStartRef.current = Date.now();
        }
      } catch {
        // Polling hatalarını sessizce yut
      }
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const fetchGroups = async () => {
    setLoading(true);
    setSelectedReport(null);
    setEmbedConfig(null);
    try {
      const environment = isTestMode ? 'test' : 'prod';
      const data = await getReportGroups(environment);
      setReportGroups(data);
      const expanded = {};
      data.forEach(g => expanded[g.id] = true);
      setExpandedGroups(expanded);
    } catch {
      setError('Raporlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  const handleReportSelect = async (report) => {
    // Önceki session varsa bitir
    if (sessionIdRef.current) {
      if (currentPageRef.current && pageStartRef.current) {
        const pageDuration = Math.floor((Date.now() - pageStartRef.current) / 1000);
        if (pageDuration > 0) {
          await logPage(sessionIdRef.current, currentPageRef.current, pageDuration);
        }
      }
      const duration = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      await endSession(sessionIdRef.current, duration);

      sessionIdRef.current = null;
      sessionStartRef.current = null;
      currentPageRef.current = null;
      pageStartRef.current = null;
    }

    setSelectedReport(report);
    setEmbedConfig(null);
    setEmbedLoading(true);

    try {
      const config = await getEmbedConfig(report.id);
      setEmbedConfig(config);

      try {
        const sessionId = await startSession(report.id);
        if (sessionId) {
          sessionIdRef.current = sessionId;
          sessionStartRef.current = Date.now();
        }
      } catch {
        // Session başlatılamazsa loglama olmadan devam et
      }
    } catch {
      setError('Rapor yüklenirken bir hata oluştu.');
    } finally {
      setEmbedLoading(false);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={{ ...styles.header, backgroundColor: brand.primary }}>
        <div style={styles.headerLeft}>
          <button onClick={() => setSidebarOpen(!sidebarOpen)} style={styles.menuButton}>☰</button>
          {brand.logo ? (
            <img src={brand.logo} alt="Logo" style={styles.logo} />
          ) : (
            <h2 style={styles.title}>{brand.title}</h2>
          )}
          {isTestMode && <span style={styles.testBadge}>TEST MODU</span>}
        </div>
        <div style={styles.userInfo}>
          <span style={styles.userName}>{user?.fullName}</span>
          {canAccessTest && (
            <div style={styles.toggleContainer}>
              <span style={styles.toggleLabel}>Test Modu</span>
              <div
                style={{ ...styles.toggle, ...(isTestMode ? { ...styles.toggleActive, backgroundColor: brand.secondary } : {}) }}
                onClick={() => dispatch(toggleTestMode())}
              >
                <div style={{ ...styles.toggleKnob, ...(isTestMode ? styles.toggleKnobActive : {}) }} />
              </div>
            </div>
          )}
          {isAdmin && (
            <button onClick={() => navigate('/admin')} style={styles.navButton}>Admin Panel</button>
          )}
          <button onClick={handleLogout} style={styles.logoutButton}>Çıkış Yap</button>
        </div>
      </div>

      <div style={styles.body}>
        {/* Sidebar */}
        {sidebarOpen && (
          <div style={{ ...styles.sidebar, backgroundColor: brand.primary + 'ee' }}>
            {loading && <p style={styles.sidebarInfo}>Yükleniyor...</p>}
            {error && <p style={styles.sidebarError}>{error}</p>}
            {!loading && reportGroups.length === 0 && (
              <p style={styles.sidebarInfo}>Rapor bulunamadı.</p>
            )}
            {reportGroups.map(group => (
              <div key={group.id}>
                <div style={styles.groupHeader} onClick={() => toggleGroup(group.id)}>
                  <span style={styles.groupIcon}>{expandedGroups[group.id] ? '▼' : '▶'}</span>
                  <span style={styles.groupName}>{group.name}</span>
                </div>
                {expandedGroups[group.id] && (
                  <div style={styles.reportList}>
                    {group.reports.map(report => (
                      <div
                        key={report.id}
                        style={{
                          ...styles.reportItem,
                          ...(selectedReport?.id === report.id ? {
                            ...styles.reportItemActive,
                            borderLeft: `2px solid ${brand.secondary}`
                          } : {})
                        }}
                        onClick={() => handleReportSelect(report)}
                      >
                        📊 {report.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Ana İçerik */}
        <div style={styles.content}>
          {selectedReport ? (
            <div style={{ height: '100%' }}>
              {embedLoading && <p style={styles.reportInfo}>Rapor yükleniyor...</p>}
              {embedConfig && (
                <PowerBIEmbed
                  embedConfig={{
                    type: 'report',
                    id: embedConfig.reportId,
                    embedUrl: embedConfig.embedUrl,
                    accessToken: embedConfig.embedToken,
                    tokenType: models.TokenType.Embed,
                    settings: {
                      panes: {
                        filters: { expanded: false, visible: false },
                        pageNavigation: { visible: true }
                      }
                    }
                  }}
                  eventHandlers={new Map([
                    ['filtersApplied', async (event, embeddedReport) => {
                      try {
                        const pages = await embeddedReport.getPages();
                        const activePage = pages.find(p => p.isActive);
                        if (activePage) {
                          const filters = await activePage.getFilters();
                          const reportFilters = await embeddedReport.getFilters();
                          const allFilters = [...reportFilters, ...filters];
                          const summary = allFilters
                            .filter(f => f.filterType === 1)
                            .map(f => `${f.target?.table}[${f.target?.column}] = ${f.values?.join(', ')}`)
                            .join('\n');
                          setActiveFilters(summary);
                        }
                      } catch {
                        // sessiz
                      }
                    }],
                    ['loaded', async (event, embeddedReport) => {
                      try {
                        const reportFilters = await embeddedReport.getFilters();
                        const summary = reportFilters
                          .filter(f => f.filterType === 1)
                          .map(f => `${f.target?.table}[${f.target?.column}] = ${f.values?.join(', ')}`)
                          .join('\n');
                        setActiveFilters(summary);
                      } catch {
                        // sessiz
                      }
                    }]
                  ])}
                  getEmbeddedComponent={(embeddedReport) => {
                    embedRef.current = embeddedReport;
                  }}
                  cssClassName="report-embed"
                />
              )}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <p>Sol menüden bir rapor seçin.</p>
            </div>
          )}
        </div>
      </div>

        {(() => {
            if (!selectedReport) return null;  // ← rapor seçilmemişse widget gösterme

            const activeGroup = reportGroups.find(g =>
              g.reports?.some(r => r.id === selectedReport?.id)
            );
            return activeGroup?.aiChatEnabled !== false ? (
              <AiChatWidget
                reportName={selectedReport?.name}
                reportId={selectedReport?.id}
                workspaceId={selectedReport?.workspaceId}
                activeFilters={activeFilters}
                primaryColor={brand.primary}
            />
          ) : null;
        })()}
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '12px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '16px' },
  menuButton: { backgroundColor: 'transparent', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer' },
  logo: { height: '32px', objectFit: 'contain' },
  title: { color: 'white', margin: 0, fontSize: '18px' },
  testBadge: { backgroundColor: '#f59e0b', color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '600' },
  userInfo: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: { color: 'white', fontSize: '14px' },
  toggleContainer: { display: 'flex', alignItems: 'center', gap: '8px' },
  toggleLabel: { color: '#94a3b8', fontSize: '13px' },
  toggle: { width: '40px', height: '22px', backgroundColor: '#4b5563', borderRadius: '11px', cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s' },
  toggleActive: { backgroundColor: '#f59e0b' },
  toggleKnob: { width: '18px', height: '18px', backgroundColor: 'white', borderRadius: '50%', position: 'absolute', top: '2px', left: '2px', transition: 'left 0.2s' },
  toggleKnobActive: { left: '20px' },
  navButton: { backgroundColor: 'transparent', border: '1px solid #94a3b8', color: '#94a3b8', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid white', color: 'white', padding: '6px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  body: { display: 'flex', flex: 1, overflow: 'hidden' },
  sidebar: { width: '260px', backgroundColor: '#1e2a3a', overflowY: 'auto', padding: '16px 0' },
  sidebarInfo: { color: '#aaa', fontSize: '13px', padding: '0 16px' },
  sidebarError: { color: 'red', fontSize: '13px', padding: '0 16px' },
  groupHeader: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', cursor: 'pointer', color: '#cbd5e1', fontSize: '13px', fontWeight: '600', userSelect: 'none' },
  groupIcon: { fontSize: '10px', color: '#94a3b8' },
  groupName: { flex: 1 },
  reportList: { paddingLeft: '8px' },
  reportItem: { padding: '8px 16px 8px 32px', cursor: 'pointer', color: '#94a3b8', fontSize: '13px', borderLeft: '2px solid transparent' },
  reportItemActive: { backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', borderLeft: '2px solid #3b82f6' },
  content: { flex: 1, overflow: 'hidden', padding: '0' },
  reportInfo: { color: '#666' },
  emptyState: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#999', fontSize: '16px' },
};

export default ReportsPage;